import React, { useState, useEffect } from 'react';
import { Zap, X, Unlock, RefreshCcw, Ticket, AlertCircle, Gem, Coins } from 'lucide-react';
import { db } from '../../services/firebase';
import { collection, getDocs, query, limit, doc, updateDoc, increment, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../hooks/useAuth';

interface MysteryDropModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenBuyCredits?: () => void;
}

type ReelSymbol = {
  id: string;
  type: 'product' | 'voucher' | 'credit';
  image?: string;
  name: string;
  brand?: string;
  businessId?: string;
  userId?: string;
};

// ============================================================================
// 1. FALLING COINS ANIMATION
// ============================================================================
const FallingLoot = () => {
  const lootItems = Array.from({ length: 25 }).map((_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    animationDelay: `${Math.random() * 5}s`,
    animationDuration: `${3 + Math.random() * 4}s`,
    size: Math.random() > 0.8 ? 32 : Math.random() > 0.5 ? 24 : 16,
    opacity: 0.1 + Math.random() * 0.5,
    type: Math.random()
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0 mask-image-b">
      {lootItems.map((item) => (
        <div
          key={item.id}
          className="absolute -top-20 animate-fall"
          style={{
            left: item.left,
            animationDelay: item.animationDelay,
            animationDuration: item.animationDuration,
            opacity: item.opacity
          }}
        >
          {item.type > 0.7 ? (
             <Coins size={item.size} className="text-yellow-500 drop-shadow-[0_0_10px_rgba(234,179,8,0.8)]" />
          ) : item.type > 0.4 ? (
             <Zap size={item.size} className="text-cyan-400 fill-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]" />
          ) : (
             <Gem size={item.size} className="text-blue-500 drop-shadow-[0_0_10px_rgba(59,130,246,0.8)]" />
          )}
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// 2. LIVE WINNERS FEED
// ============================================================================
const LiveWinnersFeed = ({ symbols }: { symbols: ReelSymbol[] }) => {
  const [winners, setWinners] = useState<ReelSymbol[]>([]);

  useEffect(() => {
    if (symbols.length === 0) return;
    const generateFakeWin = () => symbols[Math.floor(Math.random() * symbols.length)];

    setWinners([generateFakeWin(), generateFakeWin(), generateFakeWin()]);

    const interval = setInterval(() => {
      setWinners(prev => [generateFakeWin(), ...prev.slice(0, 4)]); 
    }, 4000);

    return () => clearInterval(interval);
  }, [symbols]);

  if (symbols.length === 0) return null;

  return (
    <div className="w-full max-w-md mt-6 flex flex-col gap-3 h-48 overflow-y-auto scrollbar-hide pb-safe mask-image-b z-10 relative">
      <h3 className="text-cyan-500 font-black text-[10px] uppercase tracking-[0.2em] mb-1 px-2 flex items-center gap-2 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]">
        <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse shadow-[0_0_10px_rgba(34,211,238,1)]"></span> Live Drops
      </h3>
      {winners.map((win, idx) => (
        <div key={idx} className="flex items-center gap-4 p-3 bg-zinc-950/80 backdrop-blur-md border border-cyan-900/30 rounded-2xl animate-in slide-in-from-top-4 fade-in duration-500 shadow-xl">
          <div className="w-12 h-12 rounded-xl bg-black border border-cyan-500/30 overflow-hidden shrink-0 flex items-center justify-center shadow-inner">
            {win.type === 'product' && win.image ? (
              <img src={win.image} alt={win.name} className="w-full h-full object-cover" />
            ) : win.type === 'voucher' ? (
              <Ticket className="text-indigo-400" size={24} />
            ) : (
              <Zap className="text-yellow-400 fill-yellow-400" size={24} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-zinc-500 text-[9px] font-bold uppercase tracking-widest">Anonymous won</p>
            <p className="text-white font-black text-xs uppercase truncate tracking-tight mt-0.5 drop-shadow-md">
              {win.brand ? `${win.brand} ` : ''}{win.name}
            </p>
          </div>
          <p className="text-cyan-600 text-[9px] font-mono uppercase">Just now</p>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// 3. MAIN MYSTERY DROP MODAL
// ============================================================================
export const MysteryDropModal: React.FC<MysteryDropModalProps> = ({ isOpen, onClose, onOpenBuyCredits }) => {
  const { user } = useAuth();
  const [userCredits, setUserCredits] = useState(0);
  
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [symbols, setSymbols] = useState<ReelSymbol[]>([]);
  const [reels, setReels] = useState<ReelSymbol[]>([]);
  
  // 🟢 Updated state to handle the "rebate" UI specifically
  const [lastWin, setLastWin] = useState<{ text: string; image?: string; type?: 'jackpot' | 'rebate' } | null>(null);

  const customCSS = `
    @keyframes fall {
      0% { transform: translateY(-10vh) rotate(0deg); opacity: 0; }
      10% { opacity: 1; }
      90% { opacity: 1; }
      100% { transform: translateY(110vh) rotate(360deg); opacity: 0; }
    }
    .animate-fall {
      animation: fall linear infinite;
    }
    .mask-image-b {
      mask-image: linear-gradient(to bottom, black 50%, transparent 100%);
    }
  `;

  // 1. FETCH REAL CREDITS
  useEffect(() => {
    if (!user) return;
    const unsubscribe = onSnapshot(doc(db, 'users', user.uid), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setUserCredits(data.cred || data.credits || data.balance || 0); 
      }
    });
    return () => unsubscribe();
  }, [user]);

  // 2. FETCH REAL PRODUCTS
  useEffect(() => {
    const fetchPrizes = async () => {
      try {
        const q = query(collection(db, 'products'), limit(15));
        const snapshot = await getDocs(q);
        const products = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            type: 'product' as const,
            image: data.image || data.images?.[0],
            name: data.name,
            brand: data.brand,
            businessId: data.businessId,
            userId: data.userId
          };
        });

        // 🟢 Added the 450 CRD REBATE symbol
        const mixedSymbols: ReelSymbol[] = [
          ...products,
          { id: 'v_50', type: 'voucher', name: '50% OFF STOREWIDE' },
          { id: 'v_ship', type: 'voucher', name: 'FREE SHIPPING' },
          { id: 'c_back', type: 'credit', name: '450 CRD REBATE' }, 
          { id: 'c_10k', type: 'credit', name: '10,000 CREDITS' },
          { id: 'c_50k', type: 'credit', name: '50,000 CREDITS' }
        ];

        setSymbols(mixedSymbols);
        
        if (mixedSymbols.length > 2) {
          setReels([mixedSymbols[0], mixedSymbols[1], mixedSymbols[2]]);
        }
      } catch (error) {
        console.error("Failed to load products for Mystery Drop:", error);
      }
    };
    fetchPrizes();
  }, []);

  const getRandomSymbol = () => symbols[Math.floor(Math.random() * symbols.length)];

  // 3. BULLETPROOF BUY CREDITS TRIGGER
  const triggerBuyCredits = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (onOpenBuyCredits) {
        onOpenBuyCredits();
    } else {
        window.dispatchEvent(new CustomEvent('open-wallet'));
    }
  };

  // 4. THE SPIN LOGIC
  const handleUnlock = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (!user || isUnlocking || symbols.length === 0 || userCredits < 500) return;

    setIsUnlocking(true);
    setLastWin(null);

    // Initial Deduction of 500
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        cred: increment(-500)
      });
    } catch (err) {
      console.error("Failed to deduct credits", err);
      setIsUnlocking(false);
      return;
    }

    let spins = 0;
    const maxSpins = 25; 
    const intervalTime = 80;

    const spinInterval = setInterval(() => {
      setReels([getRandomSymbol(), getRandomSymbol(), getRandomSymbol()]);
      spins++;

      if (spins >= maxSpins) {
        clearInterval(spinInterval);
        finalizeUnlock();
      }
    }, intervalTime);
  };

  // 🟢 5. THE ADDICTIVE OUTCOME ENGINE
  const finalizeUnlock = async () => {
    const roll = Math.random();
    
    const isJackpot = roll < 0.005; // 0.5% chance for a physical item/massive drop
    const isRebate = roll >= 0.005 && roll < 0.25; // ~24.5% chance for the 450 back

    let winningSymbol: ReelSymbol | null = null;

    if (isJackpot) {
      // 🏆 REAL WIN (Exclude the 450 rebate from this pool)
      const jackpotPool = symbols.filter(s => s.id !== 'c_back');
      winningSymbol = jackpotPool[Math.floor(Math.random() * jackpotPool.length)];
      
      if (winningSymbol.type === 'product') {
        setLastWin({ text: `WINNER: ${winningSymbol.brand} ${winningSymbol.name}!`, image: winningSymbol.image, type: 'jackpot' });
        if (user) {
            try {
                const orderRef = await addDoc(collection(db, "orders"), {
                    userId: user.uid,
                    businessId: winningSymbol.businessId || winningSymbol.userId || 'admin', 
                    items: [{ ...winningSymbol, quantity: 1, price: 0 }], 
                    total: 0, 
                    status: 'processing',
                    paymentStatus: 'paid',
                    paymentMethod: 'scoop_mystery_drop', 
                    customerName: user.displayName || 'Mystery Drop Winner',
                    createdAt: serverTimestamp()
                });
                await addDoc(collection(db, "notifications"), {
                    userId: winningSymbol.businessId || winningSymbol.userId || 'admin',
                    type: 'sale',
                    title: '🎰 Mystery Drop Win (Paid by Scoop)',
                    message: `Someone won your ${winningSymbol.name}! Scoop Admin has covered the cost. Please prep the order!`,
                    image: winningSymbol.image,
                    read: false,
                    relatedId: orderRef.id,
                    createdAt: serverTimestamp()
                });
            } catch (err) { console.error(err); }
        }
      } else if (winningSymbol.type === 'voucher') {
        setLastWin({ text: `You won a ${winningSymbol.name}! (Check Wallet)`, type: 'jackpot' });
        if (user) {
            try {
                await addDoc(collection(db, "user_vouchers"), {
                    userId: user.uid,
                    voucherType: winningSymbol.name.includes('50%') ? 'discount_50' : 'free_shipping',
                    name: winningSymbol.name,
                    isActive: true,
                    used: false,
                    createdAt: serverTimestamp()
                });
            } catch (err) { console.error(err); }
        }
      } else {
        // Massive Credit Win (10k / 50k)
        setLastWin({ text: `JACKPOT! You won ${winningSymbol.name}!`, type: 'jackpot' });
        if (user) {
           const amount = winningSymbol.id === 'c_50k' ? 50000 : 10000;
           await updateDoc(doc(db, 'users', user.uid), { cred: increment(amount) });
        }
      }

    } else if (isRebate) {
      // 🏆 THE REBATE LOOP (Win 450 Back)
      winningSymbol = symbols.find(s => s.id === 'c_back') || symbols[0];
      
      setLastWin({ text: `CASH BACK! +450 CRD REBATE`, type: 'rebate' });
      
      if (user) {
        try {
          await updateDoc(doc(db, 'users', user.uid), { cred: increment(450) });
        } catch (err) { console.error(err); }
      }
    }

    if (winningSymbol) {
       // Lock all 3 reels on the winning symbol
       setReels([winningSymbol, winningSymbol, winningSymbol]);
    } else {
       // 🔴 LOSS: Scramble reels so they don't match
       let s1 = getRandomSymbol();
       let s2 = getRandomSymbol();
       let s3 = getRandomSymbol();
       while (s1.id === s2.id && s2.id === s3.id) {
         s3 = getRandomSymbol(); 
       }
       setReels([s1, s2, s3]);
    }
    
    setIsUnlocking(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[600] flex flex-col items-center justify-end lg:justify-center p-0 lg:p-6 animate-in fade-in duration-300">
      <style>{customCSS}</style>
      
      {/* Dark Backdrop */}
      <div className="absolute inset-0 bg-black/95 backdrop-blur-xl" onClick={() => !isUnlocking && onClose()} />

      <div className="relative z-10 w-full h-[100dvh] lg:h-auto lg:max-w-4xl bg-black/40 lg:border border-cyan-900/50 lg:rounded-[3rem] overflow-hidden flex flex-col shadow-[0_0_100px_rgba(8,145,178,0.1)]">
        
        {/* ANIMATED BACKGROUND ENGINE */}
        <FallingLoot />

        {/* HEADER */}
        <div className="p-6 flex justify-between items-center bg-black/80 backdrop-blur-md border-b border-cyan-900/30 shrink-0 pt-safe lg:pt-6 z-20">
          <div className="flex flex-col">
            <h1 className="text-2xl lg:text-3xl font-black text-white italic uppercase tracking-tighter drop-shadow-[0_0_10px_rgba(255,255,255,0.5)]">Mystery Drop</h1>
            <p className="text-cyan-400 font-mono text-[10px] uppercase tracking-widest mt-1">Unlock The Vault</p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 bg-black border px-4 py-2 rounded-full shadow-lg transition-colors ${userCredits < 500 ? 'border-red-500/50 shadow-red-500/20' : 'border-cyan-500/50 shadow-cyan-500/20'}`}>
              <Zap size={16} className={`${userCredits < 500 ? 'text-red-400 fill-red-400' : 'text-yellow-400 fill-yellow-400'}`} />
              <span className={`font-black font-mono ${userCredits < 500 ? 'text-red-400' : 'text-white'}`}>{userCredits.toLocaleString()} CRD</span>
            </div>
            <button onClick={onClose} disabled={isUnlocking} className="p-2 bg-zinc-900/80 rounded-full text-zinc-400 hover:text-white transition-colors disabled:opacity-50 hover:bg-zinc-800">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* MACHINE AREA */}
        <div className="flex-1 flex flex-col items-center justify-center p-6 relative bg-transparent overflow-y-auto">
          
          {/* Main Blue Glowing Orb */}
          <div className={`absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] rounded-full pointer-events-none transition-all duration-1000 ${isUnlocking ? 'bg-cyan-500/40 blur-[150px] scale-110' : 'bg-blue-600/20 blur-[100px] scale-100'}`} />

          {/* NEON VAULT CASING */}
          <div className={`relative border-4 rounded-[3rem] p-6 lg:p-10 bg-black/60 backdrop-blur-2xl mb-6 shrink-0 mt-4 lg:mt-0 transition-all duration-300 z-10 ${isUnlocking ? 'border-cyan-400 shadow-[0_0_60px_rgba(34,211,238,0.5)] scale-[1.02]' : 'border-blue-600 shadow-[0_0_40px_rgba(37,99,235,0.3)]'}`}>
            
            <div className={`absolute -left-4 top-10 bottom-10 w-8 border-r-4 rounded-r-[100%] transition-colors ${isUnlocking ? 'border-cyan-400 shadow-[0_0_25px_rgba(34,211,238,0.8)]' : 'border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.5)]'}`} />
            <div className={`absolute -right-4 top-10 bottom-10 w-8 border-l-4 rounded-l-[100%] transition-colors ${isUnlocking ? 'border-cyan-400 shadow-[0_0_25px_rgba(34,211,238,0.8)]' : 'border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.5)]'}`} />

            <div className="flex items-center gap-4 lg:gap-8 bg-zinc-950 p-4 lg:p-6 rounded-2xl border-y border-zinc-800 relative overflow-hidden shadow-inner">
              <div className="absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-zinc-950 to-transparent z-10" />
              <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-zinc-950 to-transparent z-10" />

              {reels.map((symbol, index) => (
                <div key={index} className="relative flex flex-col items-center justify-center w-20 h-28 lg:w-32 lg:h-40 bg-black border border-cyan-900/30 rounded-xl lg:rounded-2xl overflow-hidden shadow-2xl">
                  
                  <div className={`transition-all duration-75 flex flex-col items-center justify-center p-2 w-full h-full ${isUnlocking ? 'animate-[pulse_0.1s_ease-in-out_infinite] blur-[2px] translate-y-6 scale-110' : ''}`}>
                    {symbol.type === 'product' && symbol.image ? (
                      <img src={symbol.image} className="w-[80%] h-[80%] object-contain drop-shadow-[0_0_15px_rgba(255,255,255,0.2)]" alt="product" />
                    ) : symbol.type === 'voucher' ? (
                      <>
                        <Ticket size={48} className="text-cyan-400 drop-shadow-[0_0_20px_rgba(34,211,238,1)] mb-2" />
                        <span className="text-[8px] lg:text-[10px] font-black text-white text-center uppercase tracking-widest">{symbol.name}</span>
                      </>
                    ) : (
                      <>
                        <Zap size={48} className={`${symbol.id === 'c_back' ? 'text-cyan-400 fill-cyan-400 drop-shadow-[0_0_20px_rgba(34,211,238,1)]' : 'text-yellow-400 fill-yellow-400 drop-shadow-[0_0_20px_rgba(250,204,21,1)]'} mb-2`} />
                        <span className="text-[8px] lg:text-[10px] font-black text-white text-center uppercase leading-tight tracking-widest">{symbol.name}</span>
                      </>
                    )}
                  </div>

                </div>
              ))}

              <div className="absolute left-[31%] lg:left-[32%] top-0 bottom-0 w-[2px] bg-blue-500/50 shadow-[0_0_10px_rgba(59,130,246,0.8)]" />
              <div className="absolute right-[31%] lg:right-[32%] top-0 bottom-0 w-[2px] bg-blue-500/50 shadow-[0_0_10px_rgba(59,130,246,0.8)]" />
            </div>
          </div>

          <div className="min-h-12 mb-6 flex flex-col items-center justify-center shrink-0 z-10">
            {/* 🟢 DYNAMIC WIN MESSAGE UI */}
            {lastWin && (
              <div className={`animate-in zoom-in slide-in-from-bottom-4 flex items-center gap-3 px-6 py-4 rounded-full backdrop-blur-md border-2 shadow-[0_0_30px_rgba(34,211,238,0.4)] ${lastWin.type === 'rebate' ? 'bg-cyan-500/20 border-cyan-400' : 'bg-blue-500/20 border-blue-400'}`}>
                {lastWin.image ? (
                  <img src={lastWin.image} alt="Prize" className="w-10 h-10 rounded-full object-cover border-2 border-cyan-300 bg-black" />
                ) : (
                  <Zap size={24} className={lastWin.type === 'rebate' ? 'text-cyan-300 fill-cyan-300' : 'text-yellow-300 fill-yellow-300'} />
                )}
                <span className="text-white font-black text-xs lg:text-sm uppercase tracking-widest leading-tight drop-shadow-md">
                  {lastWin.text}
                </span>
              </div>
            )}
            
            {userCredits < 500 && !isUnlocking && !lastWin && (
                <div className="flex items-center gap-2 text-cyan-400 bg-cyan-900/30 px-4 py-2 rounded-full border border-cyan-500/50 text-[10px] font-black uppercase tracking-widest animate-pulse shadow-[0_0_15px_rgba(34,211,238,0.2)]">
                    <AlertCircle size={14} /> Low Balance - Tap to Reload
                </div>
            )}
          </div>

          {/* 🟢 DYNAMIC ACTION BUTTON */}
          <button
            onClick={userCredits < 500 ? triggerBuyCredits : handleUnlock}
            disabled={isUnlocking}
            className={`relative group w-full max-w-xs transition-all shrink-0 z-10 ${userCredits < 500 ? 'hover:scale-105 active:scale-95' : 'active:scale-95'} disabled:opacity-50 disabled:active:scale-100`}
          >
            <div className={`absolute inset-0 blur-xl rounded-full transition-all duration-300 ${userCredits < 500 ? 'bg-cyan-500 opacity-60 group-hover:opacity-100 group-hover:blur-2xl' : 'bg-blue-600 opacity-60 group-hover:opacity-100'} ${isUnlocking ? 'opacity-20 blur-md' : ''}`} />
            
            <div className={`relative flex items-center justify-center gap-4 w-full py-5 lg:py-6 bg-black border-2 rounded-full text-white overflow-hidden transition-colors ${userCredits < 500 ? 'border-cyan-400' : 'border-blue-500 hover:border-cyan-400'}`}>
              
              <div className={`absolute inset-0 bg-gradient-to-r pointer-events-none opacity-20 ${userCredits < 500 ? 'from-cyan-600 to-blue-600' : 'from-blue-600 to-indigo-600'}`} />
              
              {isUnlocking ? (
                 <RefreshCcw className="animate-spin text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,1)]" size={28} />
              ) : userCredits < 500 ? (
                 <>
                   <Coins className="text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]" size={24} />
                   <span className="font-black uppercase tracking-[0.3em] text-lg lg:text-xl drop-shadow-[0_0_10px_rgba(255,255,255,0.8)]">Buy Credits</span>
                 </>
              ) : (
                 <>
                   <Unlock className="text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]" size={24} />
                   <span className="font-black uppercase tracking-[0.3em] text-lg lg:text-xl drop-shadow-[0_0_10px_rgba(255,255,255,0.8)]">Unlock</span>
                   <div className="flex items-center gap-1 text-cyan-400 font-mono text-sm border-l border-cyan-500/50 pl-4 bg-black/50 py-1 rounded-r-full pr-2">
                     <Zap size={14} /> 500
                   </div>
                 </>
              )}
            </div>
          </button>

          <LiveWinnersFeed symbols={symbols} />

        </div>
      </div>
    </div>
  );
};